import { useState, useEffect } from "react";
import clsx from "clsx";

import { useAtom } from "../../app-jotai";
import {
  adminDialogStateAtom,
  userDialogStateAtom,
} from "../../auth/authAtoms";
import {
  listUsers,
  updateUser,
  deleteUser,
  regenerateMFA,
} from "../../auth/authApi";
import { useHandleAppTheme } from "../../useHandleAppTheme";

import { CreateUserModal } from "./CreateUserModal";

import "./AdminDialog.scss";

interface AdminUser {
  id: string;
  username: string;
  email: string;
  role: string;
  isAdmin: boolean;
  enabled: boolean;
  createdAt: string;
  lastModified: string;
  mfaConfigured: boolean;
}

export const AdminDialog = () => {
  const [dialogState, setDialogState] = useAtom(adminDialogStateAtom);
  const [, setUserDialogState] = useAtom(userDialogStateAtom);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { editorTheme } = useHandleAppTheme();

  useEffect(() => {
    if (dialogState.isOpen) {
      loadUsers();
    }
  }, [dialogState.isOpen]);

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const userList = await listUsers();
      setUsers(userList as AdminUser[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load users");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      await updateUser(userId, { role: newRole } as any);
      await loadUsers(); // Reload to get updated data
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to update role");
    }
  };

  const handleToggleEnabled = async (userId: string, enabled: boolean) => {
    try {
      await updateUser(userId, { enabled });
      await loadUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to update user");
    }
  };

  const handleDeleteUser = async (userId: string, username: string) => {
    if (!confirm(`Are you sure you want to delete user "${username}"?`)) {
      return;
    }

    try {
      await deleteUser(userId);
      await loadUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to delete user");
    }
  };

  const handleRegenerateMFA = async (userId: string, username: string) => {
    if (
      !confirm(
        `Regenerate MFA for user "${username}"? This will invalidate their current authenticator setup.`,
      )
    ) {
      return;
    }

    try {
      await regenerateMFA(userId);
      alert(
        "MFA regenerated successfully. User will need to set up MFA again.",
      );
      await loadUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to regenerate MFA");
    }
  };

  const handleCreateUser = () => {
    setUserDialogState({ isOpen: true, mode: "add", userId: null });
  };

  const handleClose = () => {
    setDialogState({ isOpen: false });
  };

  if (!dialogState.isOpen) {
    return null;
  }

  return (
    <div
      className={clsx("excalidraw excalidraw-app", `theme--${editorTheme}`)}
      onKeyDown={(e) => e.stopPropagation()}
      onKeyUp={(e) => e.stopPropagation()}
      onKeyPress={(e) => e.stopPropagation()}
    >
      <div className="AdminDialog__backdrop" onClick={handleClose} />
      <div className="AdminDialog__modal">
        <div className="AdminDialog__header">
          <h2 className="AdminDialog__title">User Management</h2>
          <button
            className="AdminDialog__close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="AdminDialog__content">
          {error && <div className="AdminDialog__error">{error}</div>}

          <div className="AdminDialog__actions">
            <button
              className="AdminDialog__button AdminDialog__button--primary"
              onClick={handleCreateUser}
            >
              + Create New User
            </button>
          </div>

          {isLoading ? (
            <div className="AdminDialog__loading">Loading users...</div>
          ) : (
            <div className="AdminDialog__table-container">
              <table className="AdminDialog__table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>MFA</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>
                        <select
                          value={user.role || "user"}
                          onChange={(e) =>
                            handleRoleChange(user.id, e.target.value)
                          }
                          className="AdminDialog__select"
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                          <option value="owner">Owner</option>
                        </select>
                      </td>
                      <td>
                        <label className="AdminDialog__toggle">
                          <input
                            type="checkbox"
                            checked={user.enabled}
                            onChange={(e) =>
                              handleToggleEnabled(user.id, e.target.checked)
                            }
                          />
                          <span className="AdminDialog__toggle-slider" />
                        </label>
                      </td>
                      <td>
                        {user.mfaConfigured ? (
                          <span className="AdminDialog__badge AdminDialog__badge--success">
                            ✓
                          </span>
                        ) : (
                          <span className="AdminDialog__badge AdminDialog__badge--warning">
                            ✗
                          </span>
                        )}
                      </td>
                      <td>
                        <button
                          className="AdminDialog__action-btn"
                          onClick={() =>
                            handleRegenerateMFA(user.id, user.username)
                          }
                          title="Regenerate MFA"
                        >
                          🔄
                        </button>
                        <button
                          className="AdminDialog__action-btn AdminDialog__action-btn--danger"
                          onClick={() =>
                            handleDeleteUser(user.id, user.username)
                          }
                          title="Delete user"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <CreateUserModal onUserCreated={loadUsers} />
    </div>
  );
};
